// src/layout/builders/ERP/VendorPurchasePage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useERPStore from "../../../store/builders/useERPStore";
import useAuthStore from "../../../store/useAuthStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import { ITEM_UNIT_MAP, MATERIAL_CATEGORIES } from "../../../lib/materialOptions";
import { Plus, X, Trash2, Save, ArrowLeft, PackagePlus } from "lucide-react";
// Remove css if we are using tailwind fully

const ALL_UNITS = ["unit", "Bag", "CFT", "Nos", "kg", "Feet", "Meter", "Liters", "sq.ft", "Sheets", "m³"];
const ALL_UNIT_SET = new Set([...Object.values(ITEM_UNIT_MAP), ...ALL_UNITS]);

const newRow = () => ({
  _id: Math.random().toString(36).slice(2),
  item_name: "",
  quantity: "",
  unit: "unit",
  unit_rate: "",
  purchase_date: new Date().toISOString().split("T")[0],
  notes: "",
  project_id: "",
  _otherMaterial: false,
  _customUnit: false,
});

const PurchaseRow = ({ row, idx, onChange, onRemove, canRemove, projects }) => {
  const unitList = row.item_name && ITEM_UNIT_MAP[row.item_name]
    ? [ITEM_UNIT_MAP[row.item_name], ...Array.from(ALL_UNIT_SET).filter(u => u !== ITEM_UNIT_MAP[row.item_name])]
    : Array.from(ALL_UNIT_SET);

  const total = (parseFloat(row.quantity) || 0) * (parseFloat(row.unit_rate) || 0);

  const handleItemChange = (val) => {
    if (val === "__other__") {
      onChange(idx, { item_name: "", _otherMaterial: true });
      return;
    }
    const suggestedUnit = ITEM_UNIT_MAP[val];
    onChange(idx, { item_name: val, _otherMaterial: false, ...(suggestedUnit ? { unit: suggestedUnit, _customUnit: false } : {}) });
  };

  const handleUnitChange = (val) => {
    if (val === "__custom__") {
      onChange(idx, { unit: "", _customUnit: true });
      return;
    }
    onChange(idx, { unit: val, _customUnit: false });
  };

  return (
    <tr className="group border-b border-gray-100 hover:bg-blue-50/20 transition-colors">
      <td className="px-3 py-2 text-center text-[11px] font-bold text-gray-400 w-8">{idx + 1}</td>
      <td className="px-2 py-2 min-w-[160px]">
        {!row._otherMaterial ? (
          <select
            value={row.item_name}
            onChange={e => handleItemChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 bg-white text-gray-700 font-medium transition-colors"
          >
            <option value="">Select...</option>
            {row.item_name && !ITEM_UNIT_MAP[row.item_name] && row.item_name !== "__other__" && (
              <option value={row.item_name}>{row.item_name} (Custom)</option>
            )}
            {MATERIAL_CATEGORIES.map(cat => (
              <optgroup key={cat.category} label={cat.category}>
                {cat.items.map(item => <option key={item} value={item}>{item}</option>)}
              </optgroup>
            ))}
            <option value="__other__" className="font-bold text-blue-600">+ Custom Material</option>
          </select>
        ) : (
          <div className="flex gap-1">
            <input
              autoFocus
              placeholder="Type material..."
              value={row.item_name}
              onChange={e => onChange(idx, { item_name: e.target.value })}
              className="w-full border border-blue-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-500 font-medium"
            />
            <button type="button" onClick={() => onChange(idx, { item_name: "", _otherMaterial: false })}
              className="p-1 text-gray-400 hover:text-red-500 rounded shrink-0"><X size={12} /></button>
          </div>
        )}
      </td>
      <td className="px-2 py-2 w-20">
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="0"
          value={row.quantity}
          onChange={e => onChange(idx, { quantity: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 text-right font-medium"
        />
      </td>
      <td className="px-2 py-2 w-24">
        {!row._customUnit ? (
          <select
            value={row.unit}
            onChange={e => handleUnitChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 bg-white"
          >
            {unitList.map(u => <option key={u} value={u}>{u}</option>)}
            <option value="__custom__" className="font-bold text-blue-600">+ Custom</option>
          </select>
        ) : (
          <div className="flex gap-1">
            <input
              autoFocus
              placeholder="Unit..."
              value={row.unit}
              onChange={e => onChange(idx, { unit: e.target.value })}
              className="w-full border border-blue-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-500"
            />
            <button type="button" onClick={() => onChange(idx, { unit: "unit", _customUnit: false })}
              className="p-1 text-gray-400 hover:text-red-500 rounded shrink-0"><X size={12} /></button>
          </div>
        )}
      </td>
      <td className="px-2 py-2 w-24">
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
          value={row.unit_rate}
          onChange={e => onChange(idx, { unit_rate: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 text-right font-medium"
        />
      </td>
      <td className="px-2 py-2 w-24 text-right">
        <span className="font-black text-blue-700 text-xs">
          ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </td>
      <td className="px-2 py-2 w-32">
        <input
          type="date"
          value={row.purchase_date}
          onChange={e => onChange(idx, { purchase_date: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400"
        />
      </td>
      <td className="px-2 py-2 w-28">
        <select
          value={row.project_id}
          onChange={e => onChange(idx, { project_id: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 bg-white"
        >
          <option value="">Godown</option>
          {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.project_name || pr.name}</option>)}
        </select>
      </td>
      <td className="px-2 py-2 w-8 text-center">
        {canRemove && (
          <button type="button" onClick={() => onRemove(idx)}
            className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100">
            <Trash2 size={13} />
          </button>
        )}
      </td>
    </tr>
  );
};

const VendorPurchasePage = () => {
  const { vendorId, purchaseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { createPurchase, updatePurchase, fetchVendorPurchases, purchases } = useERPStore();
  
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([newRow(), newRow(), newRow(), newRow(), newRow()]);
  const [loading, setLoading] = useState(false);

  // Load projects
  useEffect(() => {
    if (user?.id) {
      axiosInstancev1
        .get(`/projects?builderId=${user.id}&status=active&limit=1000&page=1`)
        .then((r) => setProjects(r.data?.projects || []))
        .catch(() => {});
    }
  }, [user?.id]);

  // Load purchase data if editing
  useEffect(() => {
    if (purchaseId && purchases.length > 0) {
      const purchase = purchases.find((p) => p.id === Number(purchaseId));
      if (purchase) {
        setRows([{
          _id: Math.random().toString(36).slice(2),
          item_name: purchase.item_name || "",
          quantity: purchase.quantity || "",
          unit: purchase.unit || "unit",
          unit_rate: purchase.unit_rate || "",
          purchase_date: purchase.purchase_date?.split("T")[0] || "",
          notes: purchase.notes || "",
          project_id: purchase.project_id || "",
          _otherMaterial: purchase.item_name && !ITEM_UNIT_MAP[purchase.item_name],
          _customUnit: purchase.unit && !ALL_UNIT_SET.has(purchase.unit),
        }]);
      }
    } else if (!purchaseId && rows.length === 1) {
       // if we came from edit to new
       setRows([newRow(), newRow(), newRow(), newRow(), newRow()]);
    }
  }, [purchaseId, purchases]);

  const handleRowChange = (idx, updates) => {
    const updated = [...rows];
    updated[idx] = { ...updated[idx], ...updates };
    setRows(updated);
  };

  const addRow = () => setRows([...rows, newRow()]);
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    // Only process rows that have item_name selected/typed
    const validRows = rows.filter(r => r.item_name && r.item_name.trim() !== "");
    if (validRows.length === 0) {
      alert("Please fill in at least one item.");
      return;
    }

    setLoading(true);
    let allOk = true;

    for (const row of validRows) {
      const totalAmt = (parseFloat(row.quantity) || 0) * (parseFloat(row.unit_rate) || 0);
      const payload = {
        item_name: row.item_name,
        quantity: row.quantity,
        unit: row.unit,
        unit_rate: row.unit_rate,
        total_amount: totalAmt.toString(),
        paid_amount: "0", // Removed from UI as requested
        outstanding: totalAmt.toString(), // Initially all is outstanding
        purchase_date: row.purchase_date,
        project_id: row.project_id,
        notes: row.notes,
        vendor_id: Number(vendorId),
      };

      let ok;
      if (purchaseId) {
        ok = await updatePurchase(Number(purchaseId), payload);
      } else {
        ok = await createPurchase(payload);
      }
      if (!ok) allOk = false;
    }

    setLoading(false);
    if (allOk) {
      await fetchVendorPurchases(vendorId);
      navigate(`/erp/vendor/${vendorId}`);
    } else {
      alert("Some purchases failed to save. Please check the ledger.");
    }
  };

  const handleCancel = () => navigate(`/erp/vendor/${vendorId}`);

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={handleCancel} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-800">{purchaseId ? "Edit Purchase" : "Add Purchase Entries"}</h2>
            <p className="text-xs text-gray-500 font-medium">Record materials billed by vendor</p>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between bg-blue-600 px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <PackagePlus size={18} />
            <h3 className="font-bold text-sm">{purchaseId ? "Edit Purchase Entry" : "Add Purchase Entries"}</h3>
            {!purchaseId && <span className="bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full ml-2">{rows.length} rows</span>}
          </div>
        </div>
        
        <div className="overflow-x-auto p-4">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="px-3 py-2 text-center text-[10px] font-extrabold text-gray-500 uppercase tracking-wider w-8">#</th>
                <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">ITEM / MATERIAL *</th>
                <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider text-center w-20">QTY</th>
                <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider w-24">UNIT</th>
                <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider text-right w-24">RATE (₹)</th>
                <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider text-right w-24">TOTAL</th>
                <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider w-32">DATE</th>
                <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider w-28">PROJECT</th>
                <th className="px-2 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <PurchaseRow
                  key={row._id}
                  row={row}
                  idx={idx}
                  onChange={handleRowChange}
                  onRemove={removeRow}
                  canRemove={rows.length > 1}
                  projects={projects}
                />
              ))}
            </tbody>
          </table>
          
          {!purchaseId && (
            <div className="mt-4">
              <button
                type="button"
                onClick={addRow}
                className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-[11px] font-bold border border-blue-200 border-dashed rounded-lg px-4 py-2 hover:bg-blue-50 transition-colors"
              >
                <Plus size={14} /> Add Another Row
              </button>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 p-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={handleCancel}
            className="px-5 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
          >
            <Save size={16} />
            {loading ? "Saving..." : "Save Purchases"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorPurchasePage;

